/** Wires ports for the current mode (real API vs in-browser mock) and restores the offline cache. */
import {
  NotificationsViewModel,
  Onboarding,
  SessionViewModel,
  type AuthClient,
  type KeyValueStorage,
  type RealtimeClient,
  type WorkspaceCache,
  type WorkspaceRef,
} from "@bundle/core";
import type { QueryClient } from "@tanstack/query-core";
import { AnalyticsService } from "./analytics.ts";
import { createHttpApi, setServerErrorReporter, type WebApi } from "./api.ts";
import { webPasswordSignIn } from "./accountSecurity.ts";
import { onSignIn, takeSignInMarker, webAuth } from "./auth.ts";
import { importWatch } from "./telemetryApi.ts";
import { systemClock } from "./clock.ts";
import { QueryDataSource } from "./data/dataSource.ts";
import { keys } from "./data/keys.ts";
import { createPersister, createQueryClient, restoreAndPersist } from "./data/queryClient.ts";
import { WebSnapshots } from "./data/snapshots.ts";
import { isDesktop } from "./desktop.ts";
import { createDesktopAuth } from "./desktopAuth.ts";
import { API_BASE, APP_VERSION, MOCK, STORAGE_NAMESPACE } from "./env.ts";
import { initialLocale } from "./preferences.tsx";
import { webPush } from "./push.ts";
import { desktopPush, Notifier } from "./notifier.ts";
import { browserRealtime } from "./realtime.ts";
import { createEditorDocs } from "./docs/index.ts";
import { networkMonitor } from "./network.ts";
import type { EditorDocs, PasswordSignIn } from "@bundle/core";
import { createStorage } from "./storage.ts";
import { createFilePlatform, type WebFilePlatform } from "./files/actions.ts";
import { MediaUrls } from "./files/media.ts";
import { UploadManager } from "./files/uploads.ts";
import { WarmWorkspaces, type WarmEntry } from "../shell/warmWorkspaces.ts";

export type Container = {
  api: WebApi;
  realtime: RealtimeClient;
  storage: KeyValueStorage;
  queryClient: QueryClient;
  data: QueryDataSource;
  /** What opens at once (Mail's inbox, Home's digest, the threads I've read), on this device. */
  snapshots: WebSnapshots;
  session: SessionViewModel;
  /** GA4, consent-gated (see analytics.ts). */
  analytics: AnalyticsService;
  /** "Enable notifications" (web push) for this device. */
  notifications: NotificationsViewModel;
  /** Sign out from a menu: unregisters this device's push token first (needs the session), then signs out. */
  signOut: () => Promise<void>;
  /** Page bodies (Loro CRDT, lazy-loaded). */
  docs: EditorDocs;
  /** Files: the upload queue (IndexedDB), the media pipeline for file URLs (PrimitivesProvider) and file actions. */
  uploads: UploadManager;
  media: MediaUrls;
  files: WebFilePlatform;
  /** Desktop/web notifications from the realtime connection, and the app-icon badge (notifier.ts). */
  notifier: Notifier;
  /** The workspaces opened this session, kept warm for an instant switch (shell/warmWorkspaces.ts). */
  warm: WarmWorkspaces<WarmEntry>;
  mock: boolean;
};

/**
 * Analytics for API calls (no content, no ids): a created workspace, a sent message (channel kind and whether it's a
 * thread reply, from the cache), a sent email, and mail imports (started; completed when a connection I saw importing
 * moves on to syncing). Same object otherwise, so session hooks and tokens are shared.
 */
function withAnalytics(api: WebApi, analytics: AnalyticsService, channelKind: (ws: string, channelId: string) => string | undefined): WebApi {
  return {
    ...api,
    async createWorkspace(req) {
      const res = await api.createWorkspace(req);
      analytics.track({ name: "workspace_created" });
      return res;
    },
    async postMessage(ws, channelId, req) {
      const message = await api.postMessage(ws, channelId, req);
      const kind = channelKind(ws, channelId);
      const channelType = kind === "public" || kind === "private" || kind === "dm" || kind === "group_dm" ? kind : "unknown";
      analytics.track({ name: "message_sent", params: { channel_type: channelType, thread: req.threadRootId !== undefined } });
      return message;
    },
    async sendMail(ws, mailboxId, req) {
      const res = await api.sendMail(ws, mailboxId, req);
      analytics.track({ name: "mail_sent" });
      return res;
    },
    async createMailSync(ws, req) {
      const connection = await api.createMailSync(ws, req);
      importWatch(analytics).started(connection.id, connection.kind);
      return connection;
    },
    async listMailSync(ws) {
      const res = await api.listMailSync(ws);
      importWatch(analytics).connections(res.connections);
      return res;
    },
  };
}

/** The URL names the open workspace (/{slug}/…): keep it in step when the session opens another one. */
function showWorkspaceInUrl(ws: WorkspaceRef): void {
  if (Onboarding.workspaceSlugFromPath(location.pathname) === ws.slug) return;
  // Leaving an invite page replaces it (Back shouldn't land on a used invite); otherwise it's a navigation.
  if (location.pathname.startsWith("/invite/")) history.replaceState(null, "", `/${ws.slug}`);
  else history.pushState(null, "", `/${ws.slug}`);
}

export async function createContainer(): Promise<Container> {
  const storage = createStorage();
  let api: WebApi;
  let realtime: RealtimeClient;
  let auth: AuthClient;
  let password: PasswordSignIn | undefined;
  if (MOCK) {
    // Mock mode is code-split so the real app never ships the fake server.
    const [{ createMockApi, mockAuth }, { mockRealtime }, { createMockSignIn }] = await Promise.all([
      import("./mock/api.ts"),
      import("./mock/realtime.ts"),
      import("./mock/accountSecurity.ts"),
    ]);
    const mockApi = createMockApi();
    api = mockApi;
    realtime = mockRealtime;
    // Passwords, two-step, reset, passkeys and email codes, ending in a mock session (mock/accountSecurity.ts).
    const mock = createMockSignIn(mockAuth, (email, name) => mockApi.devLogin(email, name));
    auth = mock.auth;
    password = mock.password;
  } else {
    api = createHttpApi();
    realtime = browserRealtime;
    // Desktop: sign-in goes through the system browser and Rust keeps the session (apps/desktop/INTEGRATION.md §7).
    auth = isDesktop() ? createDesktopAuth() : webAuth;
    // Desktop signs in through the system browser (every method, passkeys included, runs there).
    password = isDesktop() ? undefined : webPasswordSignIn;
  }
  const analytics = new AnalyticsService(storage);
  if (!MOCK) setServerErrorReporter((description) => analytics.exception(description));
  // The regional default (prompt or on) needs the request country: the public sign-in methods carry it.
  if (analytics.needsCountry) {
    api.authMethods().then(
      (m) => analytics.setCountry(m.country),
      () => analytics.setCountry(undefined),
    );
  }
  let queryClientRef: QueryClient | undefined;
  api = withAnalytics(api, analytics, (ws, channelId) => queryClientRef?.getQueryData<WorkspaceCache>(keys.workspace(ws))?.channels[channelId]?.kind);
  onSignIn((method, isNew) => analytics.track({ name: isNew ? "sign_up" : "login", params: { method } }));
  const google = takeSignInMarker();
  if (google !== undefined) analytics.track({ name: google.isNew ? "sign_up" : "login", params: { method: google.method } });
  // The session restores the token first so resumed (persisted) mutations are authenticated.
  const session = new SessionViewModel({ api, auth, storage, clock: systemClock, onWorkspaceOpened: showWorkspaceInUrl, ...(password === undefined ? {} : { password }) });
  // Refresh before the token expires; on 401 refresh once and retry (then sign out).
  api.setSessionHooks({ ensureFresh: () => session.ensureFreshToken(), rejected: (token) => session.authRejected(token) });
  const queryClient = createQueryClient(() => void session.authRejected());
  queryClientRef = queryClient;
  const snapshots = new WebSnapshots();
  await Promise.all([restoreAndPersist(queryClient, createPersister(), api), snapshots.hydrate()]);
  const data = new QueryDataSource(queryClient, api, () => session.state.session?.userId ?? "");
  const uploads = new UploadManager(api, networkMonitor, STORAGE_NAMESPACE);
  data.enableFiles(uploads);
  const media = new MediaUrls(api, API_BASE, uploads, storage);
  if (MOCK) {
    const { mockFiles } = await import("./mock/files.ts");
    media.mockFile = (key) => mockFiles().url(key);
    void mockFiles().ready.then(() => media.refresh());
  }
  const files = createFilePlatform(media);
  // Desktop: notifications are the app's own (over the WebSocket), so turning them on only asks for the permission.
  let notifier: Notifier | undefined;
  // The OS permission arrives asynchronously (after this constructor), and again on focus.
  const notifications: NotificationsViewModel = new NotificationsViewModel({
    push: isDesktop() ? desktopPush(() => notifier?.confirm(), () => notifications.refresh()) : webPush,
    data,
    storage,
    clock: systemClock,
    platform: isDesktop() ? "desktop" : "web",
    userId: () => session.state.session?.userId,
    locale: () => initialLocale(storage),
    appVersion: APP_VERSION,
    timeZone: () => Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  // Signing out (or in as someone else) drops the previous person's cached workspaces and queued writes, and their
  // push registration on this device.
  let lastUser = session.state.session?.userId;
  analytics.identify(lastUser);
  const warm = new WarmWorkspaces<WarmEntry>();
  let lastWorkspaces = session.state.workspaces;
  session.subscribe(() => {
    const user = session.state.session?.userId;
    // A workspace I left (or was removed from) isn't kept warm.
    const list = session.state.workspaces;
    if (list !== lastWorkspaces && user === lastUser) {
      lastWorkspaces = list;
      if (list !== undefined && user !== undefined) warm.retain((key) => list.some((w) => key === `${user}:${w.id}`));
    }
    if (user === lastUser) return;
    lastWorkspaces = list;
    analytics.identify(user);
    if (lastUser !== undefined) {
      warm.clear();
      void data.clear();
      void snapshots.clear();
      media.clear();
    }
    lastUser = user;
    notifications.sessionChanged();
    void notifications.sync();
  });
  void notifications.sync();
  // Permission can change in browser settings while the app is open.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") notifications.refresh();
  });
  const signOut = async () => {
    await Promise.race([notifications.signOut(), new Promise((r) => setTimeout(r, 3000))]);
    session.signOut();
  };
  // Page bodies (Loro): the docs chunk loads on the first page opened.
  const docs = createEditorDocs({ api, network: networkMonitor, mock: MOCK, namespace: STORAGE_NAMESPACE });
  notifier = new Notifier({ notifications, webPushAvailable: () => webPush.availability() === "available", locale: () => initialLocale(storage) });
  return { api, realtime, storage, queryClient, data, snapshots, session, analytics, notifications, notifier, signOut, docs, uploads, media, files, warm, mock: MOCK };
}
